import { MONTHS } from '@/common/constants';
import { ITableColumn, ITableHeaderRow, ITableMonthData } from '../models/IStewardshipTable';

const HEADER_BG = 'FFF3F4F6';
const MONTH_BG = 'FFF3F4F6';
const ACTION_BG = 'FFF3F4F6';
const ROW_ODD_BG = 'FFF9FAFB';
const ROW_EVEN_BG = 'FFFFFFFF';
const BORDER_COLOR = 'FFD1D5DB';

const border = () => ({
    top: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
    left: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
    bottom: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
    right: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
});

const SUB_ROW_LABELS = [
    'Mas\'ul foydalanuvchilar soni',
    'Tekshirilgan',
    'Tekshirilmagan',
];

export async function exportStewardshipToExcel(
    columns: ITableColumn[],
    headerRows: ITableHeaderRow[],
    monthsData: ITableMonthData[],
    filename = 'mas\'ul-xodimlar',
    regionName?: string,
) {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    const wsName = regionName ? `Mas'ul xodimlar ${regionName}` : 'Mas\'ul xodimlar';
    const ws = wb.addWorksheet(wsName);

    const totalCols = 1 + columns.length;

    ws.columns = [
        { width: 36 },
        ...columns.map(() => ({ width: 18 })),
    ];

    // ─── HEADER ROW ────────────────────────────────────────────────────────────
    const headerValues = ['', ...columns.map((c) => c.name)];
    const hRow = ws.addRow(headerValues);
    hRow.height = 40;
    hRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true, size: 10 };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
        cell.border = border();
        if (colNumber > 1) {
            cell.font = { bold: true, size: 10, color: { argb: 'FF1E40AF' } };
        }
    });

    // ─── HEADER DATA ROWS (e.g. "Umumiy loyihalar soni") ────────────────────
    headerRows.forEach((headerRow) => {
        const values: (string | number)[] = [
            headerRow.label,
            ...columns.map((col) => {
                const val = headerRow.getValue(col);
                return typeof val === 'number' ? val : Number(val) || 0;
            }),
        ];
        const row = ws.addRow(values);
        row.height = 22;
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ROW_EVEN_BG } };
            cell.border = border();
            if (colNumber === 1) {
                cell.alignment = { horizontal: 'left', vertical: 'middle' };
                cell.font = { size: 10 };
            } else {
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.font = { size: 10, color: { argb: 'FF1E40AF' } };
            }
        });
    });

    // ─── MONTHS + ACTIONS ───────────────────────────────────────────────────────
    let dataRowIdx = 0;

    monthsData.forEach((monthData) => {
        // Month header
        const monthLabel = MONTHS.find((m) => m.value === monthData.month)?.label || monthData.month;
        const monthRow = ws.addRow([monthLabel, ...columns.map(() => '')]);
        monthRow.height = 28;
        for (let c = 1; c <= totalCols; c++) {
            const cell = ws.getCell(monthRow.number, c);
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: MONTH_BG } };
            cell.border = border();
            cell.font = { bold: true, size: 10 };
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
        }
        ws.mergeCells(monthRow.number, 1, monthRow.number, totalCols);

        // Actions
        monthData.actions.forEach((action, actionIdx) => {
            // Action name header row
            const actionRow = ws.addRow([`${actionIdx + 1}. ${action.action_name}`, ...columns.map(() => '')]);
            actionRow.height = 24;
            for (let c = 1; c <= totalCols; c++) {
                const cell = ws.getCell(actionRow.number, c);
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ACTION_BG } };
                cell.border = border();
                cell.font = { bold: true, size: 10 };
                cell.alignment = { horizontal: 'left', vertical: 'middle' };
            }
            ws.mergeCells(actionRow.number, 1, actionRow.number, totalCols);

            // 3 sub-rows: responsible_users_count, completed_actions_count, pending_actions_count
            const subRowKeys = ['responsible_users_count', 'completed_actions_count', 'pending_actions_count'] as const;

            subRowKeys.forEach((key, subIdx) => {
                const rowBg = dataRowIdx % 2 === 0 ? ROW_EVEN_BG : ROW_ODD_BG;
                const values: (string | number)[] = [
                    `    ${SUB_ROW_LABELS[subIdx]}`,
                    ...columns.map((col) => {
                        const colData = action.columnData[col.id] || {
                            responsible_users_count: 0,
                            completed_actions_count: 0,
                            pending_actions_count: 0,
                        };
                        return colData[key];
                    }),
                ];
                const row = ws.addRow(values);
                row.height = 22;
                row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
                    cell.border = border();
                    if (colNumber === 1) {
                        cell.alignment = { horizontal: 'left', vertical: 'middle' };
                        cell.font = { size: 10, color: { argb: 'FF6B7280' } };
                    } else {
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                        cell.font = { size: 10, color: { argb: 'FF1E40AF' } };
                    }
                });
                dataRowIdx++;
            });
        });
    });

    // ─── FREEZE PANES ──────────────────────────────────────────────────────────
    ws.views = [{ state: 'frozen', xSplit: 1, ySplit: 1 }];

    // ─── DOWNLOAD ──────────────────────────────────────────────────────────────
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
}
